<template>
  <section class="event-intro">
    <div class="event-intro__card">
      <div class="event-intro__gallery">
        <EntityGallery :images="gallery" :alt="detail.title" :height="320" />
      </div>

      <div class="event-intro__content">
        <h2 class="event-intro__title">{{ detail.title }}</h2>
        <dl class="event-intro__meta">
          <div
            v-for="field in fields"
            :key="field.label"
            class="event-intro__meta-row"
            :class="{ 'event-intro__meta-row--source': isSourceField(field) }"
          >
            <dt>{{ field.label }}：</dt>
            <dd :class="{ 'event-intro__source-dd': isSourceField(field) }">
              <template v-if="isSourceField(field) && sourceLinks(field).length">
                <template v-for="(link, index) in sourceLinks(field)">
                  <router-link
                    v-if="link.resourceId"
                    :key="`${field.label}-${link.resourceId}-${index}`"
                    :to="`/resources/${link.resourceId}`"
                    class="event-intro__source-link"
                  >
                    {{ link.text }}
                  </router-link>
                  <span v-else :key="`${field.label}-text-${index}`" class="event-intro__source-text">{{ link.text }}</span>
                </template>
              </template>
              <template v-else>{{ field.value }}</template>
            </dd>
          </div>
        </dl>
        <div class="event-intro__desc-block">
          <h4 class="event-intro__desc-title">{{ detail.introLabel || '事件介绍' }}</h4>
          <p class="event-intro__desc">{{ eventDescription.value }}</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { fetchEntityRelatedResources } from '@/api/knowledge'
import { fetchResourcesByIds, mapResourceItem, searchResources } from '@/api/resources'
import {
  filterEventIntroFields,
  isSourceField,
  matchResourceBySourceName,
  resolveSourceLinks,
  sourceSearchKeyword
} from '@/utils/entityMeta'
import EntityGallery from './EntityGallery.vue'

export default {
  name: 'EventIntro',
  components: { EntityGallery },
  props: {
    detail: { type: Object, required: true }
  },
  data() {
    return {
      relatedResources: [],
      sourceResources: [],
      resolvedSourceLinks: {}
    }
  },
  computed: {
    gallery() {
      return this.detail.gallery || []
    },
    fields() {
      const raw = (this.detail.fields || []).filter((field) => (
        field.code !== 'eventDescription' && field.code !== 'event_summary'
      ))
      const limited = filterEventIntroFields(raw)
      return limited.length ? limited : raw.slice(0, 4)
    },
    eventDescription() {
      return this.detail.fields.find(field => field.code === 'eventDescription') || { value: '' }
    },
    resourceLookup() {
      const map = new Map()
      ;[...this.relatedResources, ...this.sourceResources].forEach((item) => {
        if (item && item.id != null) {
          map.set(Number(item.id), item)
        }
      })
      return Array.from(map.values())
    },
    sourceResolveKey() {
      return this.fields
        .filter((field) => this.isSourceField(field))
        .map((field) => `${field.label}:${field.value}:${(field.resourceLinks || []).length}`)
        .join('|')
    }
  },
  watch: {
    'detail.id': {
      immediate: true,
      handler(id) {
        this.loadRelatedResources(id)
      }
    },
    sourceResolveKey: {
      immediate: true,
      handler() {
        this.resolveSourceFields()
      }
    }
  },
  methods: {
    isSourceField,
    sourceLinks(field) {
      const key = field && field.label
      if (key && this.resolvedSourceLinks[key] && this.resolvedSourceLinks[key].length) {
        return this.resolvedSourceLinks[key]
      }
      return resolveSourceLinks(field, this.resourceLookup)
    },
    async loadRelatedResources(entityId) {
      if (!entityId) {
        this.relatedResources = []
        return
      }
      try {
        const { records } = await fetchEntityRelatedResources({
          entityId,
          page: 1,
          limit: 50
        })
        this.relatedResources = records || []
      } catch (error) {
        this.relatedResources = []
      }
      this.resolveSourceFields()
    },
    async resolveSourceFields() {
      const next = {}
      const pendingSearches = []
      const idSet = new Set()

      this.fields.forEach((field) => {
        if (!this.isSourceField(field)) {
          return
        }
        const links = resolveSourceLinks(field, this.resourceLookup).map((link) => ({ ...link }))
        links.forEach((link, index) => {
          if (link.resourceId) {
            idSet.add(Number(link.resourceId))
            return
          }
          const matched = matchResourceBySourceName(link.text, this.resourceLookup)
          if (matched && matched.id) {
            link.resourceId = matched.id
            link.text = matched.title || link.text
            idSet.add(Number(matched.id))
            return
          }
          pendingSearches.push({ fieldLabel: field.label, index, text: link.text })
        })
        next[field.label] = links
      })

      if (idSet.size) {
        const missingIds = [...idSet].filter((id) => (
          !this.relatedResources.some((item) => Number(item.id) === id)
          && !this.sourceResources.some((item) => Number(item.id) === id)
        ))
        if (missingIds.length) {
          try {
            const records = await fetchResourcesByIds(missingIds)
            const mapped = (records || []).map(mapResourceItem)
            this.sourceResources = [...this.sourceResources, ...mapped]
            Object.keys(next).forEach((label) => {
              next[label] = next[label].map((link) => {
                if (!link.resourceId) return link
                const found = mapped.find((item) => Number(item.id) === Number(link.resourceId))
                return found ? { ...link, text: found.title || link.text } : link
              })
            })
          } catch (error) {
            // ignore
          }
        }
      }

      if (pendingSearches.length) {
        const keywordMap = new Map()
        pendingSearches.forEach((item) => {
          const keyword = sourceSearchKeyword(item.text)
          if (!keyword) return
          if (!keywordMap.has(keyword)) {
            keywordMap.set(keyword, [])
          }
          keywordMap.get(keyword).push(item)
        })

        await Promise.all([...keywordMap.entries()].map(async ([keyword, items]) => {
          try {
            const { records } = await searchResources({ page: 1, limit: 10, keyword })
            const mapped = (records || []).map(mapResourceItem)
            if (mapped.length) {
              this.sourceResources = [...this.sourceResources, ...mapped]
            }
            items.forEach((item) => {
              const matched = matchResourceBySourceName(item.text, mapped)
                || matchResourceBySourceName(item.text, this.resourceLookup)
              if (!matched || !matched.id) return
              const links = next[item.fieldLabel]
              if (!links || !links[item.index]) return
              links[item.index] = {
                text: matched.title || item.text,
                resourceId: matched.id
              }
            })
          } catch (error) {
            // ignore search failures
          }
        }))
      }

      this.resolvedSourceLinks = next
    }
  }
}
</script>

<style lang="scss" scoped>
.event-intro {
  margin-bottom: $spacing-3xl;

  &__card {
    display: flex;
    gap: $spacing-xl;
    padding: $spacing-xl;
    background: $color-white;
    border-radius: 4px;
    box-shadow: 0 4px 24px rgba($color-brown-dark, 0.08);
  }

  &__gallery {
    width: 48%;
    flex-shrink: 0;
  }

  &__content {
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-family: $font-serif;
    font-size: rem(28);
    font-weight: 700;
    color: $color-brown-dark;
    margin-bottom: $spacing-base;
  }

  &__meta {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: $spacing-xs $spacing-xl;
    margin-bottom: $spacing-base;
    padding-bottom: $spacing-base;
    border-bottom: 1px solid rgba($color-border, 0.5);
  }

  &__meta-row {
    display: flex;
    font-size: rem(14);
    line-height: 1.8;

    dt { color: $color-text-light; flex-shrink: 0; }
    dd { color: $color-text; min-width: 0; }

    &--source {
      grid-column: 1 / -1;
    }
  }

  &__source-dd {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }

  &__source-link {
    position: relative;
    z-index: 1;
    display: inline-block;
    max-width: 100%;
    color: $color-brown-dark;
    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 2px;
    line-height: 1.5;

    &:hover {
      color: darken($color-brown-dark, 8%);
    }
  }

  &__source-text {
    display: inline-block;
    max-width: 100%;
  }

  &__desc-title {
    font-size: rem(15);
    font-weight: 600;
    color: $color-brown-dark;
    margin-bottom: $spacing-sm;
  }

  &__desc {
    font-size: rem(14);
    line-height: 1.9;
    color: $color-text-light;
  }
}

@media (max-width: 960px) {
  .event-intro__card {
    flex-direction: column;

    .event-intro__gallery { width: 100%; }
    .event-intro__meta { grid-template-columns: 1fr; }
  }
}
</style>
